import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Ban, Pencil } from "lucide-react";
import { get, post } from "@/lib/api";
import { fmtDateHeure, fmtDuree, fmtNum } from "@/lib/format";
import { DataTable } from "@/components/common/DataTable";
import { LoadingState, ErrorState } from "@/components/common/States";
import { CrudDialog } from "@/components/admin/Referentiel";
import { cn } from "@/lib/utils";

const TYPE_LABEL = { production: "Production", rejet: "Rejet", arret: "Arrêt" };
const TYPE_TONE = { production: "text-emerald-400", rejet: "text-orange-300", arret: "text-red-400" };

// ISO UTC -> valeur d'un <input type="datetime-local"> en heure LOCALE du navigateur (même fuseau que l'affichage).
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso), p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const useCorrectionsInvalidation = () => {
  const qc = useQueryClient();
  return () => ["of-evenements", "of-detail", "of", "dashboard", "kpi-jour", "kpi-planificateur", "corrections-recentes", "trs"]
    .forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
};

export const SaisiesPanel = ({ ofId, data, unite, decimales = 0, peutCorriger }) => {
  const [masquer, setMasquer] = useState(false);
  const [dlg, setDlg] = useState(null); // { mode: "corriger" | "annuler", e }
  const invalider = useCorrectionsInvalidation();
  const params = useQuery({ queryKey: ["corrections-params"], queryFn: () => get("/corrections/parametres"), staleTime: 60000 });
  const motifsRejet = useQuery({ queryKey: ["motifs-rejet"], queryFn: () => get("/motifs-rejet"), staleTime: 60000, enabled: !!peutCorriger });
  const catsArret = useQuery({ queryKey: ["categories-arret"], queryFn: () => get("/categories-arret"), staleTime: 60000, enabled: !!peutCorriger });

  const run = useMutation({
    mutationFn: ({ url, body }) => post(url, body),
    onSuccess: (_, v) => { toast.success(v.ok); invalider(); setDlg(null); },
    onError: (e) => toast.error(e.message),
  });

  const lignes = useMemo(() => (data?.evenements || []).filter((e) => !(masquer && e.annule)).slice().reverse(), [data, masquer]);
  const motifsOpts = (params.data?.motifs || []).map((m) => ({ value: m.code, label: m.libelle }));
  const nbAnnules = (data?.evenements || []).filter((e) => e.annule).length;

  const valeur = (e) => {
    if (e.type === "production") return <span className="text-emerald-400">+{fmtNum(e.quantite, decimales)} {unite}</span>;
    if (e.type === "rejet") return <span>{fmtNum(e.quantite, decimales)} {unite} <span className="text-orange-300">· {e.motif}</span></span>;
    const dur = e.fin ? fmtDuree((new Date(e.fin) - new Date(e.debut)) / 1000) : null;
    return <span><span className="text-red-400">{e.categorie}</span> · {fmtDateHeure(e.debut)} → {e.fin ? fmtDateHeure(e.fin) : <b className="text-red-400">en cours</b>}{dur && <span className="text-muted-foreground"> ({dur})</span>}</span>;
  };

  const etat = (e) => {
    if (e.annule && e.remplace_par) return <span className="text-amber-400" data-testid={`saisie-etat-${e.type}-${e.id}`}>Corrigée → n° {e.remplace_par}</span>;
    if (e.annule) return <span className="text-red-400" data-testid={`saisie-etat-${e.type}-${e.id}`}>Annulée</span>;
    if (e.correction_de) return <span className="text-sky-400" data-testid={`saisie-etat-${e.type}-${e.id}`}>Corrigée (remplace n° {e.correction_de})</span>;
    return <span className="text-muted-foreground" data-testid={`saisie-etat-${e.type}-${e.id}`}>Active</span>;
  };

  const cols = [
    { key: "id", header: "N°", mono: true, render: (e) => <span className={cn(e.annule && "line-through text-muted-foreground")}>{e.id}</span> },
    { key: "type", header: "Type", render: (e) => <span className={cn("font-semibold", TYPE_TONE[e.type])}>{TYPE_LABEL[e.type]}</span> },
    { key: "quand", header: "Quand", mono: true, render: (e) => fmtDateHeure(e.quand) },
    { key: "valeur", header: "Saisie", cellClassName: "whitespace-normal", render: (e) => <span className={cn(e.annule && "line-through text-muted-foreground")}>{valeur(e)}</span> },
    { key: "saisi_par", header: "Saisi par" },
    { key: "etat", header: "État", render: etat },
    { key: "motif", header: "Motif de la correction", cellClassName: "whitespace-normal text-muted-foreground min-w-[220px]", render: (e) => (e.annule
      ? <span data-testid={`saisie-motif-${e.type}-${e.id}`}>{e.motif_libelle || "—"}{e.motif_commentaire && ` — ${e.motif_commentaire}`}{e.annule_par && <span className="text-[10px]"> · par {e.annule_par}, {fmtDateHeure(e.annule_a)}</span>}</span> : "—") },
  ];
  if (peutCorriger) cols.push({ key: "actions", header: "", align: "right", render: (e) => (
    <div className="flex justify-end gap-1">
      {e.corrigeable && <button data-testid={`saisie-corriger-${e.type}-${e.id}`} title="Corriger" onClick={() => setDlg({ mode: "corriger", e })} className="h-6 px-1.5 text-[10px] font-mono uppercase border border-border rounded-sm hover:bg-accent flex items-center gap-1"><Pencil className="h-3 w-3" />Corriger</button>}
      {e.annulable && <button data-testid={`saisie-annuler-${e.type}-${e.id}`} title="Annuler cette saisie" onClick={() => setDlg({ mode: "annuler", e })} className="h-6 px-1.5 text-[10px] font-mono uppercase border border-border rounded-sm hover:bg-red-950/50 text-red-400 flex items-center gap-1"><Ban className="h-3 w-3" />Annuler</button>}
    </div>) });

  // ------------------------------------------------------------------ formulaire du dialogue
  // Motif proposé par défaut selon la correction (jamais « le premier de la liste » : il serait validé par habitude et fausserait l'historique).
  const motifFields = (defaut) => [
    { key: "motif_code", label: "Motif", type: "select", required: true, default: defaut, span: true },
    { key: "commentaire", label: "Commentaire explicatif", required: true, span: true, placeholder: "Que s'est-il passé ? (obligatoire)", help: "Ce commentaire est conservé avec la correction, dans l'historique de l'OF." },
  ];
  const construire = () => {
    if (!dlg) return null;
    const { mode, e } = dlg, base = `/of/${ofId}/${e.type}/${e.id}`;
    if (mode === "annuler") return {
      title: `Annuler la saisie n° ${e.id} (${TYPE_LABEL[e.type].toLowerCase()})`, submitLabel: "Annuler la saisie", fields: motifFields("autre"), initial: {},
      send: (f) => ({ url: `${base}/annuler`, body: { motif_code: f.motif_code, commentaire: f.commentaire }, ok: "Saisie annulée" }),
    };
    if (e.type === "production") return {
      title: `Corriger la production n° ${e.id}`, submitLabel: "Corriger",
      fields: [{ key: "quantite_bonnes", label: `Quantité bonne corrigée (${unite})`, type: "number", required: true, span: true, help: `Valeur actuelle : ${fmtNum(e.quantite, decimales)} ${unite}` }, ...motifFields("erreur_saisie_quantite")],
      initial: { quantite_bonnes: e.quantite },
      send: (f) => ({ url: `${base}/corriger`, body: { quantite_bonnes: Number(f.quantite_bonnes), motif_code: f.motif_code, commentaire: f.commentaire }, ok: "Production corrigée" }),
    };
    if (e.type === "rejet") return {
      title: `Corriger le rejet n° ${e.id}`, submitLabel: "Corriger",
      fields: [{ key: "quantite", label: `Quantité rejetée (${unite})`, type: "number", required: true }, { key: "motif_id", label: "Motif de rejet", type: "select", numeric: true, required: true }, ...motifFields("motif_rejet_incorrect")],
      initial: { quantite: e.quantite, motif_id: e.motif_id },
      send: (f) => ({ url: `${base}/corriger`, body: { quantite: Number(f.quantite), motif_id: Number(f.motif_id), motif_code: f.motif_code, commentaire: f.commentaire }, ok: "Rejet corrigé" }),
    };
    const ini = { categorie_id: e.categorie_id, debut: toLocalInput(e.debut), fin: toLocalInput(e.fin) };
    return {
      title: `Corriger l'arrêt n° ${e.id}`, submitLabel: "Corriger",
      fields: [{ key: "categorie_id", label: "Catégorie d'arrêt", type: "select", numeric: true, required: true, span: true },
        { key: "debut", label: "Début", type: "datetime-local", required: true }, { key: "fin", label: "Fin", type: "datetime-local", required: true }, ...motifFields("arret_horaires_incorrects")],
      initial: ini,
      send: (f) => ({ url: `${base}/corriger`, ok: "Arrêt corrigé", body: {
        categorie_id: Number(f.categorie_id) !== e.categorie_id ? Number(f.categorie_id) : undefined,
        debut: f.debut !== ini.debut ? new Date(f.debut).toISOString() : undefined,   // on n'envoie que ce qui a changé
        fin: f.fin !== ini.fin ? new Date(f.fin).toISOString() : undefined,
        motif_code: f.motif_code, commentaire: f.commentaire } }),
    };
  };
  // MÉMORISÉ, et INDÉPENDANT des listes chargées : CrudDialog réinitialise la saisie quand `fields`/`initial` changent.
  // Ni le rafraîchissement automatique (15 s) ni l'arrivée tardive d'une liste (réseau lent) ne doivent effacer ce que
  // l'utilisateur est en train de taper. Les listes passent par `optionsFor`, qui ne touche pas à la définition du formulaire.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const optionsFor = (key) => key === "motif_code" ? motifsOpts
    : key === "motif_id" ? (motifsRejet.data || []).filter((m) => m.actif !== false).map((m) => ({ value: m.id, label: m.nom }))
    : key === "categorie_id" ? (catsArret.data || []).filter((c) => c.actif !== false).map((c) => ({ value: c.id, label: c.nom })) : undefined;
  const d = useMemo(construire, [dlg, unite, decimales, ofId]);

  return (
    <div data-testid="saisies-panel">
      {data && !data.corrections_possibles && (
        <div data-testid="saisies-bloque" className="px-3 py-2 text-xs border-b border-border text-amber-300 bg-amber-500/10">Corrections impossibles sur cet OF : {data.raison}.</div>
      )}
      <div className="px-3 py-1.5 flex items-center justify-between gap-3 border-b border-border/60 text-[11px] text-muted-foreground">
        <span>Chaque saisie est conservée : une correction annule l'ancienne et en crée une nouvelle. Rien n'est supprimé.{data?.corrections_possibles && ` Corrections possibles jusqu'à ${data.delai_jours} jour(s) après la clôture.`}</span>
        {nbAnnules > 0 && <label className="flex items-center gap-1.5 shrink-0"><input type="checkbox" data-testid="saisies-masquer" checked={masquer} onChange={(e) => setMasquer(e.target.checked)} />Masquer les annulées ({nbAnnules})</label>}
      </div>
      <DataTable testId="saisies-table" columns={cols} rows={lignes} rowKey={(e) => `${e.type}-${e.id}`} emptyTitle="Aucune saisie" />
      {d && <CrudDialog open onClose={() => setDlg(null)} title={d.title} submitLabel={d.submitLabel} fields={d.fields} initial={d.initial} optionsFor={optionsFor} busy={run.isPending}
        onSubmit={(f) => run.mutate(d.send(f))} />}
    </div>
  );
};

export const SaisiesChargement = ({ q }) => (q.isLoading ? <LoadingState rows={3} /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : null);
